<script setup lang="ts">
/**
 * In-app Terms / Privacy modal. Prefetches and caches HTML so opening
 * does not collapse/expand while content arrives.
 */
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useStore } from 'vuex'
import DashboardModal from '@/components/dashboard/ui/DashboardModal.vue'

const { t } = useI18n()
const store = useStore()

type LegalDocKey = 'terms' | 'privacy'

const legalDocContent = ref<HTMLElement | null>(null)
const isLoading = ref(false)
const loadError = ref(false)

/** Keep fetched bodies so reopen does not jump through a loading state. */
const contentCache: Record<LegalDocKey, string | null> = {
  terms: null,
  privacy: null,
}

const isTermsOpen = computed(() => store.state.termsModalIsVisible)
const isPrivacyOpen = computed(() => store.state.privacyModalIsVisible)
const isOpen = computed(() => isTermsOpen.value || isPrivacyOpen.value)

const activeDocKey = computed<LegalDocKey>(() =>
  isTermsOpen.value ? 'terms' : 'privacy'
)

const modalContent = computed(() => {
  if (isTermsOpen.value) {
    return {
      title: t('LoginRegister.Footer.TermsModal.Header'),
      mutation: 'SET_TERMS_MODAL_VISIBILITY' as const,
      link: import.meta.env.VITE_LEGAL_TERMS_LINK,
      backupLink: import.meta.env.VITE_LEGAL_TERMS_LINK_BACKUP,
    }
  }

  return {
    title: t('LoginRegister.Footer.PrivacyModal.Header'),
    mutation: 'SET_PRIVACY_MODAL_VISIBILITY' as const,
    link: import.meta.env.VITE_LEGAL_PRIVACY_LINK,
    backupLink: import.meta.env.VITE_LEGAL_PRIVACY_LINK_BACKUP,
  }
})

const closeModal = () => {
  if (isTermsOpen.value) {
    store.commit('SET_TERMS_MODAL_VISIBILITY', false)
  }
  if (isPrivacyOpen.value) {
    store.commit('SET_PRIVACY_MODAL_VISIBILITY', false)
  }
}

const extractLegalHtml = async (url: string): Promise<string> => {
  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP error ${response.status}`)
  }

  const htmlContent = await response.text()
  const parser = new DOMParser()
  const parsedDoc = parser.parseFromString(htmlContent, 'text/html')
  const targetContent = parsedDoc.querySelector('#swiftaid-legal-doc-target-content')

  if (!targetContent) {
    throw new Error('Legal document content not found')
  }

  return targetContent.innerHTML
}

const paintLegalHtml = async (html: string) => {
  // Modal mounts via Teleport + v-if — wait for the doc node before writing.
  await nextTick()
  if (legalDocContent.value) {
    legalDocContent.value.innerHTML = html
  }
}

const loadDoc = async (key: LegalDocKey, url: string, paint: boolean) => {
  if (!contentCache[key]) {
    contentCache[key] = await extractLegalHtml(url)
  }
  if (paint) {
    await paintLegalHtml(contentCache[key] as string)
  }
}

const fetchLegalContent = async () => {
  const key = activeDocKey.value
  const { link } = modalContent.value
  loadError.value = false

  // Cached: paint immediately so the modal never opens mid-jolt.
  if (contentCache[key]) {
    isLoading.value = false
    await paintLegalHtml(contentCache[key] as string)
    return
  }

  isLoading.value = true
  if (legalDocContent.value) {
    legalDocContent.value.innerHTML = ''
  }

  try {
    await loadDoc(key, link, true)
  } catch (error) {
    loadError.value = true
    console.error('Failed to load legal document', error)
  } finally {
    isLoading.value = false
  }
}

/** Warm both documents in the background so footer clicks feel instant. */
const prefetchLegalDocs = async () => {
  const jobs: Array<Promise<void>> = []
  const termsUrl = import.meta.env.VITE_LEGAL_TERMS_LINK
  const privacyUrl = import.meta.env.VITE_LEGAL_PRIVACY_LINK

  if (termsUrl && !contentCache.terms) {
    jobs.push(
      loadDoc('terms', termsUrl, false).catch((error) => {
        console.warn('Prefetch terms failed', error)
      })
    )
  }
  if (privacyUrl && !contentCache.privacy) {
    jobs.push(
      loadDoc('privacy', privacyUrl, false).catch((error) => {
        console.warn('Prefetch privacy failed', error)
      })
    )
  }

  await Promise.all(jobs)
}

watch(isOpen, (open) => {
  if (open) {
    fetchLegalContent()
  }
})

onMounted(() => {
  prefetchLegalDocs()
})
</script>

<template>
  <DashboardModal
    :is-open="isOpen"
    overlay-class="sa-modal-overlay--legal"
    modal-class="sa-modal--legal"
    aria-labelledby="dashboard-legal-title"
    @close="closeModal"
  >
    <h2 id="dashboard-legal-title" class="sa-modal--legal__title">
      {{ modalContent.title }}
    </h2>

    <div class="sa-modal--legal__body">
      <p
        v-if="isLoading"
        class="sa-modal--legal__status sa-modal--legal__status--loading"
        role="status"
      >
        {{ $t('Dashboard.LegalModal.Loading') }}
      </p>
      <p
        v-else-if="loadError"
        class="sa-modal--legal__status sa-modal--legal__status--error"
        role="alert"
      >
        {{ $t('Dashboard.LegalModal.LoadError') }}
        <a
          class="sa-link sa-modal--legal__fallback-link"
          :href="modalContent.backupLink"
          target="_blank"
          rel="noopener noreferrer"
        >
          {{ $t('Dashboard.LegalModal.ViewInNewWindow') }}
        </a>
      </p>
      <div
        ref="legalDocContent"
        class="sa-legal-doc"
        :class="{ 'sa-legal-doc--hidden': isLoading || loadError }"
      />
    </div>

    <template #footer>
      <div class="sa-modal-actions sa-modal--legal__actions">
        <button type="button" class="sa-btn-primary" @click="closeModal">
          {{ $t('Buttons.Close') }}
        </button>
      </div>
    </template>
  </DashboardModal>
</template>
